import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import TerserahApi from '../../helpers/TerserahApi';
import Cookies from 'js-cookie';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

const Button = styled.button`
  padding: ${props => props.size === 'small' ? '6px 16px' : '10px 20px'};
  font-size: ${props => props.size === 'small' ? '14px' : '15px'};
  font-weight: 500;
  border: none;
  border-radius: 20px;
  cursor: pointer;
  transition: all 0.2s;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  
  ${props => props.isFollowing ? `
    background: #f0f2f5;
    color: #65676b;
    border: 1px solid #ddd;
    
    &:hover {
      background: #fee;
      color: #e74c3c;
      border-color: #e74c3c;
    }
  ` : `
    background: linear-gradient(135deg, #25D366 0%, #20BA5A 100%);
    color: white;
    box-shadow: 0 2px 8px rgba(37, 211, 102, 0.3);
    
    &:hover {
      transform: translateY(-1px);
      box-shadow: 0 4px 12px rgba(37, 211, 102, 0.4);
    }
  `}
  
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }

  i {
    font-size: ${props => props.size === 'small' ? '12px' : '14px'};
  }
`;

const Count = styled.span`
  margin-left: 4px;
  font-size: ${props => props.size === 'small' ? '12px' : '13px'};
  opacity: 0.8;
`;

const FollowButton = ({ userId, userName, size = 'normal', showCount = false, onFollowChange }) => {
  const api = TerserahApi();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [isFollowing, setIsFollowing] = useState(false);
  const [loading, setLoading] = useState(true);
  const [followerCount, setFollowerCount] = useState(0);
  const [isHovered, setIsHovered] = useState(false);

  const isLoggedIn = () => !!Cookies.get('token');
  const currentUserId = Cookies.get('user_public_id');

  useEffect(() => {
    if (isLoggedIn() && userId && currentUserId !== userId) {
      loadFollowStatus();
    } else {
      setLoading(false);
    }
    // eslint-disable-next-line
  }, [userId]);

  useEffect(() => {
    if (userId && showCount) {
      loadFollowerCount();
    }
    // eslint-disable-next-line
  }, [userId, showCount]);

  const loadFollowStatus = async () => {
    try {
      const json = await api.checkFollowStatus(userId);
      if (!json.error) {
        setIsFollowing(json.isFollowing);
      }
    } catch (error) {
      console.error('Error loading follow status:', error);
    }
    setLoading(false);
  };

  const loadFollowerCount = async () => {
    try {
      const json = await api.getFollowCounts(userId);
      if (!json.error) {
        setFollowerCount(json.followers || 0);
      }
    } catch (error) {
      console.error('Error loading follower count:', error);
    }
  };

  const handleFollow = async () => {
    if (!isLoggedIn()) {
      navigate('/signin');
      return;
    }

    setLoading(true);
    try {
      if (isFollowing) {
        const json = await api.unfollowUser(userId);
        if (!json.error) {
          setIsFollowing(false);
          if (showCount) setFollowerCount(prev => Math.max(0, prev - 1));
          if (onFollowChange) onFollowChange(false);
        }
      } else {
        const json = await api.followUser(userId);
        if (!json.error) {
          setIsFollowing(true);
          if (showCount) setFollowerCount(prev => prev + 1);
          if (onFollowChange) onFollowChange(true);
        }
      }
    } catch (error) {
      console.error('Error toggling follow:', error);
    }
    setLoading(false);
  };

  // Don't show button if it's the current user
  if (currentUserId === userId) {
    return null;
  }

  if (!isLoggedIn()) {
    return (
      <Button
        onClick={() => navigate('/signin')}
        size={size}
        isFollowing={false}
      >
        <i className="fas fa-user-plus"></i>
        {t('follow') || 'Seguir'}
        {showCount && followerCount > 0 && (
          <Count size={size}>({followerCount})</Count>
        )}
      </Button>
    );
  }

  return (
    <Button
      onClick={handleFollow}
      disabled={loading}
      isFollowing={isFollowing}
      size={size}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      {loading ? (
        <>
          <i className="fas fa-spinner fa-spin"></i>
          {t('loading') || 'Cargando...'}
        </>
      ) : isFollowing ? (
        <>
          {isHovered ? (
            <>
              <i className="fas fa-user-times"></i>
              {t('unfollow') || 'Dejar de seguir'}
            </>
          ) : (
            <>
              <i className="fas fa-check"></i>
              {t('following') || 'Siguiendo'}
            </>
          )}
        </>
      ) : (
        <>
          <i className="fas fa-user-plus"></i>
          {t('follow') || 'Seguir'}
        </>
      )}
      {showCount && followerCount > 0 && (
        <Count size={size}>({followerCount})</Count>
      )}
    </Button>
  );
};

export default FollowButton;
